import { captureException } from '@sentry/react'
import { CommentTool, commentToolOverrides } from '@tldraw/commenting'
import { getLicenseKey } from '@tldraw/dotcom-shared'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import {
	DefaultDebugMenu,
	DefaultDebugMenuContent,
	Editor,
	TLComponents,
	TLSessionStateSnapshot,
	TLUiDialogsContextType,
	Tldraw,
	TldrawUiMenuItem,
	createSessionStateSnapshotSignal,
	createDebugValue,
	debugFlags,
	react,
	throttle,
	tltime,
	useAtom,
	useDialogs,
	useEditor,
	useEvent,
	useToasts,
	useValue,
} from 'tldraw'
import { SneakyMermaidHandler } from '../../../components/SneakyMermaidHandler/SneakyMermaidHandler'
import { ThemeUpdater } from '../../../components/ThemeUpdater/ThemeUpdater'
import { useOpenUrlAndTrack } from '../../../hooks/useOpenUrlAndTrack'
import { usePerformanceTracking } from '../../../hooks/usePerformanceTracking'
import { estimateFileSizeBucket, useRoomLoadTracking } from '../../../hooks/useRoomLoadTracking'
import { trackEvent, useHandleUiEvents } from '../../../utils/analytics'
import { assetUrls } from '../../../utils/assetUrls'
import { createAssetFromUrl } from '../../../utils/createAssetFromUrl'
import { embedShapeUtils } from '../../../utils/embedShapeUtil'
import { reportFileLoad } from '../../../utils/fileLoad'
import { markFirstLoad, reportFirstLoad } from '../../../utils/firstLoad'
import { globalEditor } from '../../../utils/globalEditor'
import { LOADS_DEBUG_FLAG } from '../../../utils/loadTracker'
import { TldrawApp } from '../../app/TldrawApp'
import { useMaybeApp } from '../../hooks/useAppState'
import { useIsCommentingEnabled } from '../../hooks/useIsCommentingEnabled'
import { ReadyWrapper, useSetIsReady } from '../../hooks/useIsReady'
import { useNewRoomCreationTracking } from '../../hooks/useNewRoomCreationTracking'
import { useShareLinkOpenTracking } from '../../hooks/useShareLinkOpenTracking'
import { useTldrawCurrentUser } from '../../hooks/useUser'
import { defineMessages, useMsg } from '../../utils/i18n'
import { maybeSlurp } from '../../utils/slurping'
import { TlaAnonDotDevLink } from '../TlaAnonDotDevLink/TlaAnonDotDevLink'
import { CommentsOnCanvas, SignInToComment, useAnonCommentToolOverrides } from './CommentsOnCanvas'
import { TlaEditorErrorFallback } from './editor-components/TlaEditorErrorFallback'
import { TlaEditorMenuPanel } from './editor-components/TlaEditorMenuPanel'
import { TlaEditorSharePanel } from './editor-components/TlaEditorSharePanel'
import { TlaEditorTopPanel } from './editor-components/TlaEditorTopPanel'
import { SneakyCommentDeepLink } from './sneaky/SneakyCommentDeepLink'
import { SneakyDarkModeSync } from './sneaky/SneakyDarkModeSync'
import { SneakyDebugModeToast } from './sneaky/SneakyDebugModeToast'
import { SneakyTldrawFileDropHandler } from './sneaky/SneakyFileDropHandler'
import { SneakyLargeFileHander } from './sneaky/SneakyLargeFileHandler'
import { SneakySetDocumentTitle } from './sneaky/SneakySetDocumentTitle'
import { SneakyToolSwitcher } from './sneaky/SneakyToolSwitcher'
import { A11yAudit } from './TlaDebug'
import { TlaEditorWrapper } from './TlaEditorWrapper'
import { useFileLoad, useFileSyncStore } from './TlaFileSyncHost'
import { useExtraDragIconOverrides } from './useExtraToolDragIcons'
import { useFileEditorOverrides } from './useFileEditorOverrides'

const messages = defineMessages({
	slurpFailed: {
		defaultMessage: 'Could not restore your local drawing. Try reloading this page.',
	},
})

// Composing needs a signed-in author and an editable canvas. Signed-out visitors on an
// editable canvas get a sign-in prompt where the composers would be; view-only sessions
// read threads without composers (the server rejects their writes in the authorizers).
const tlaCommentTools = [
	CommentTool.configure({
		canComment: ({ editor, currentUserId }) => currentUserId !== null && !editor.getIsReadonly(),
		components: { ComposerFallback: SignInToComment },
	}),
]

/** @internal */
export const components: TLComponents = {
	ErrorFallback: TlaEditorErrorFallback,
	MenuPanel: TlaEditorMenuPanel,
	TopPanel: TlaEditorTopPanel,
	SharePanel: TlaEditorSharePanel,
	Dialogs: null,
	Toasts: null,
	// No loading screen on tla editors: the editor only mounts once it's ready,
	// so a spinner would only flash before the content appears.
	LoadingScreen: null,
}

interface TlaEditorProps {
	fileSlug: string
	isEmbed?: boolean
	deepLinks?: boolean
}

export function TlaEditor(props: TlaEditorProps) {
	// force re-mount when the file slug changes to prevent state from leaking between files
	return (
		<>
			<SneakySetDocumentTitle />
			<ReadyWrapper key={props.fileSlug}>
				<TlaEditorInner {...props} key={props.fileSlug} />
			</ReadyWrapper>
		</>
	)
}

function TlaEditorInner({ fileSlug, deepLinks, isEmbed = false }: TlaEditorProps) {
	const fileLoad = useFileLoad()
	fileLoad.mark('editor-rendered')
	markFirstLoad('editor-rendered')
	const handleUiEvent = useHandleUiEvents()
	const app = useMaybeApp()

	const fileId = fileSlug

	const setIsReady = useSetIsReady()
	const { addToast } = useToasts()
	const slurpFailedMsg = useMsg(messages.slurpFailed)
	const showSlurpFailure = useEvent(() => {
		addToast({
			id: 'local-file-restore-failed',
			severity: 'warning',
			title: slurpFailedMsg,
			keepOpen: true,
		})
	})

	const dialogs = useDialogs()
	// need to wrap this in a useEvent to prevent the context id from changing on us
	const addDialog: TLUiDialogsContextType['addDialog'] = useEvent((dialog) =>
		dialogs.addDialog(dialog)
	)

	// We cycle this flag to cause shapes to remount when slurping images/videos fails.
	// Because in that case we want to show the failure state for the images/videos.
	// i.e. where it appears that they are not present. so the user knows which ones failed.
	// There's probably a better way of doing this but I couldn't think of one.
	const hideAllShapes = useAtom('hideAllShapes', false)
	const getShapeVisibility = useCallback(
		() => (hideAllShapes.get() ? 'hidden' : 'inherit'),
		[hideAllShapes]
	)
	const remountImageShapes = useCallback(() => {
		hideAllShapes.set(true)
		requestAnimationFrame(() => {
			hideAllShapes.set(false)
		})
	}, [hideAllShapes])

	const store = useFileSyncStore()

	const trackRoomLoaded = useRoomLoadTracking()
	const trackNewRoomCreation = useNewRoomCreationTracking()
	const trackShareLinkOpen = useShareLinkOpenTracking()
	const trackPerformance = usePerformanceTracking()

	const handleMount = useCallback(
		(editor: Editor) => {
			markFirstLoad('editor-mounted')
			fileLoad.mark('editor-mounted')
			trackRoomLoaded(editor)
			trackNewRoomCreation(app, fileId)
			trackShareLinkOpen(app, fileId, isEmbed)
			const cleanupPerf = trackPerformance(editor)
			;(window as any).app = app
			;(window as any).editor = editor
			// Register the editor globally
			globalEditor.set(editor)

			// Register the external asset handler
			editor.registerExternalAssetHandler('url', createAssetFromUrl)

			if (!app) {
				setIsReady()
				markFirstLoad('board-visible')
				fileLoad.mark('board-visible')
				reportFirstLoad({ email: null, flagEnabled: false, trackEvent, fileLoad })
				reportFileLoad(fileLoad, {
					email: null,
					flagEnabled: false,
					trackEvent,
					extra: { file_size_bucket: estimateFileSizeBucket(editor) },
				})
				return
			}

			const fileState = app.getFileState(fileId)
			const deepLink = new URLSearchParams(window.location.search).get('d')
			let sessionState: TLSessionStateSnapshot | null = null
			if (fileState?.lastSessionState) {
				try {
					sessionState = JSON.parse(fileState.lastSessionState.trim() || 'null')
				} catch (err) {
					// A corrupt stored session state must not take the whole board down with it.
					captureException(err, {
						tags: { operation: 'parse-session-state' },
						extra: { fileId },
					})
				}
			}
			if (sessionState && deepLink) {
				// When using a deep link, only load preferences (not camera/page states)
				// since the deep link will control navigation
				const { pageStates: _, currentPageId: _cpid, ...preferencesOnly } = sessionState
				editor.loadSnapshot({ session: preferencesOnly }, { forceOverwriteSessionState: true })
			} else if (sessionState) {
				// No deep link - load the full session state including camera position
				editor.loadSnapshot({ session: sessionState }, { forceOverwriteSessionState: true })
			}
			if (deepLink) {
				// Let the SDK parse the link: a malformed `?d=` then zooms to fit instead of
				// throwing out of onMount and replacing the board with an error page.
				editor.navigateToDeepLink()
			}
			const fileStateUpdater = new FileStateUpdater(app, fileId, editor)

			const abortController = new AbortController()
			maybeSlurp({
				app,
				editor,
				fileId,
				abortSignal: abortController.signal,
				addDialog,
				remountImageShapes,
			})
				.catch((err) => {
					// ReadyWrapper keeps the editor invisible and inert until setIsReady runs, so a
					// failed slurp must not stop it from running.
					console.error('Failed to slurp local file', err)
					captureException(err, {
						tags: { operation: 'slurp-local-file' },
						extra: { fileId },
					})
					if (!abortController.signal.aborted) showSlurpFailure()
				})
				.then(() => {
					// A restore aborted by navigating away still resolves; the board it belonged to never
					// showed, so it must not take the one-shot report from the next one.
					if (abortController.signal.aborted) return
					setIsReady()
					markFirstLoad('board-visible')
					fileLoad.mark('board-visible')
					reportFirstLoad({
						email: app?.email,
						flagEnabled: app?.isLoadRumEnabled ?? false,
						trackEvent,
						fileLoad,
					})
					reportFileLoad(fileLoad, {
						email: app?.email,
						flagEnabled: app?.isLoadRumEnabled ?? false,
						trackEvent,
						extra: { file_size_bucket: estimateFileSizeBucket(editor) },
					})
				})

			return () => {
				cleanupPerf()
				fileStateUpdater.dispose()
				abortController.abort()
			}
		},
		[
			addDialog,
			fileLoad,
			trackRoomLoaded,
			trackNewRoomCreation,
			trackShareLinkOpen,
			trackPerformance,
			app,
			fileId,
			isEmbed,
			remountImageShapes,
			setIsReady,
			showSlurpFailure,
		]
	)

	// we need to prevent recording the file exit if the store is in an error state
	const storeError = useRef(false)
	if (store.status === 'error') {
		storeError.current = true
	}

	// Handle entering and exiting the file, with some protection against rapid enters/exits
	useEffect(() => {
		if (!app) return
		if (store.status !== 'synced-remote') return
		let didEnter = false
		let timer: number

		const fileState = app.getFileState(fileId)

		if (fileState && fileState.firstVisitAt) {
			// If there's a file state already then wait a second before marking it as entered
			timer = tltime.setTimeout(
				'file enter timer',
				() => {
					app.onFileEnter(fileId)
					didEnter = true
				},
				1000
			)
		} else {
			// If there's not a file state yet (i.e. if we're visiting this for the first time) then do an enter
			app.onFileEnter(fileId)
			didEnter = true
		}

		return () => {
			clearTimeout(timer)
			if (didEnter && !storeError.current) {
				app.updateFileState(fileId, { lastVisitAt: Date.now() })
			}
		}
	}, [app, fileId, store.status])

	const overrides = useFileEditorOverrides({ fileSlug })
	const extraDragIconOverrides = useExtraDragIconOverrides()
	const anonCommentToolOverrides = useAnonCommentToolOverrides()
	const commentingEnabled = useIsCommentingEnabled()

	const instanceComponents = useMemo((): TLComponents => {
		return {
			...components,
			DebugMenu: () => <CustomDebugMenu />,
			InFrontOfTheCanvas: commentingEnabled
				? () => <CommentsOnCanvas fileId={fileId} />
				: undefined,
		}
	}, [fileId, commentingEnabled])

	// The comment tool overrides stay in for signed-out visitors too: `anonCommentToolOverrides`
	// turns their button into the sign-in dialog. On read-only canvases the button and `c` shortcut
	// hide via the UI's readonly handling, and composing is gated by the tool's `canComment`.
	const editorOverrides = useMemo(
		() => [overrides, extraDragIconOverrides, commentToolOverrides, anonCommentToolOverrides],
		[overrides, extraDragIconOverrides, anonCommentToolOverrides]
	)

	return (
		<TlaEditorWrapper>
			<Tldraw
				className="tla-editor"
				licenseKey={getLicenseKey()}
				store={store}
				assetUrls={assetUrls}
				shapeUtils={embedShapeUtils}
				tools={commentingEnabled ? tlaCommentTools : undefined}
				user={app?.tlUser}
				onMount={handleMount}
				onUiEvent={handleUiEvent}
				components={instanceComponents}
				options={{
					actionShortcutsLocation: 'toolbar',
					deepLinks: deepLinks ? true : undefined,
				}}
				overrides={editorOverrides}
				getShapeVisibility={getShapeVisibility}
			>
				<ThemeUpdater />
				<SneakyDarkModeSync />
				<SneakyToolSwitcher />
				<SneakyMermaidHandler />
				{app && <SneakyTldrawFileDropHandler />}
				<SneakyLargeFileHander />
				<SneakyDebugModeToast />
				{commentingEnabled && <SneakyCommentDeepLink />}
				<TlaAnonDotDevLink />
			</Tldraw>
		</TlaEditorWrapper>
	)
}

const DOTCOM_DEBUG_FLAGS = {
	...debugFlags,
	logLoads: createDebugValue(LOADS_DEBUG_FLAG, { defaults: { all: false } }),
}

function CustomDebugMenu() {
	const app = useMaybeApp()
	const user = useTldrawCurrentUser()
	const openAndTrack = useOpenUrlAndTrack('unknown')
	const editor = useEditor()
	const isReadOnly = useValue('isReadOnly', () => editor.getIsReadonly(), [editor])
	return (
		<DefaultDebugMenu>
			<A11yAudit />
			{!isReadOnly && app && user?.isTldraw && (
				<TldrawUiMenuItem
					id="user-manual"
					label="File history"
					readonlyOk
					onSelect={() => {
						const url = new URL(window.location.href)
						url.pathname += '/history'
						openAndTrack(url.toString())
					}}
				/>
			)}
			<DefaultDebugMenuContent customDebugFlags={DOTCOM_DEBUG_FLAGS} />
		</DefaultDebugMenu>
	)
}

const FILE_STATE_UPDATE_INTERVAL = 10_000
class FileStateUpdater {
	disposables = new Set<() => void>()
	constructor(
		private readonly app: TldrawApp,
		private readonly fileId: string,
		editor: Editor
	) {
		this.disposables.add(
			editor.store.listen(
				() => {
					this.didDocumentChange = true
					this.update()
				},
				{ scope: 'document', source: 'user' }
			)
		)
		const flush = () => {
			this.update.flush()
		}
		window.addEventListener('beforeunload', flush)
		this.disposables.add(() => {
			window.removeEventListener('beforeunload', flush)
		})

		const sessionState$ = createSessionStateSnapshotSignal(editor.store)
		let firstTime = true
		this.disposables.add(
			react('update session state', () => {
				const state = sessionState$.get()
				if (firstTime) {
					firstTime = false
					return
				}
				if (!state) return
				this.nextSessionState = state
				this.update()
			})
		)
	}

	private nextSessionState: TLSessionStateSnapshot | null = null
	private didDocumentChange = false

	private update = throttle(
		() => {
			if (!this.nextSessionState && !this.didDocumentChange) return
			const state = this.nextSessionState
			this.nextSessionState = null
			const didChange = this.didDocumentChange
			this.didDocumentChange = false
			this.app.updateFileState(this.fileId, {
				lastSessionState: state ? JSON.stringify(state) : undefined,
				lastEditAt: didChange ? Date.now() : undefined,
				lastVisitAt: Date.now(),
			})
		},
		FILE_STATE_UPDATE_INTERVAL,
		{ trailing: true, leading: false }
	)

	dispose() {
		this.update.flush()
		this.disposables.forEach((dispose) => dispose())
		this.disposables.clear()
	}
}
